import React, { useState, useRef, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  InputAdornment,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Dashboard as BoardIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  boardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  boardNameDisplay: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    borderRadius: 6,
    cursor: "pointer",
    transition: "background 0.15s",
    userSelect: "none",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.06)"
          : "rgba(0,0,0,0.04)",
    },
  },
  boardIcon: {
    fontSize: 20,
    color: theme.palette.primary.main,
  },
  boardNameText: {
    fontWeight: 600,
    fontSize: "1rem",
    color: theme.palette.text.primary,
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  editField: {
    minWidth: 200,
    "& .MuiOutlinedInput-root": {
      borderRadius: 6,
    },
    "& input": {
      fontWeight: 600,
      fontSize: "1rem",
      padding: "6px 10px",
    },
  },
  select: {
    minWidth: 180,
    "& .MuiSelect-select": {
      fontWeight: 500,
    },
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    minWidth: 320,
  },
  templateOption: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "all 0.15s",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.05)"
          : "rgba(0,0,0,0.03)",
    },
  },
  templateSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(33,150,243,0.1)"
        : "rgba(33,150,243,0.06)",
  },
  templateTitle: {
    fontWeight: 600,
    fontSize: "0.88rem",
  },
  templateDesc: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
}));

const TEMPLATES = [
  {
    id: "status",
    title: "Por Status",
    desc: "3 colunas: Abertos, Pendentes, Fechados",
  },
  {
    id: "empty",
    title: "Em Branco",
    desc: "Painel vazio - configure suas colunas",
  },
];

const BoardSelector = ({
  boards = [],
  currentBoardId,
  onSelectBoard,
  onCreateBoard,
  onRenameBoard,
  onDeleteBoard,
}) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [template, setTemplate] = useState("status");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Rename dialog state (simpler than inline — always works)
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState("");

  const currentBoard = boards.find((b) => b.id === currentBoardId);

  const handleOpenRename = useCallback(() => {
    if (!currentBoard) return;
    setRenameName(currentBoard.name);
    setRenameOpen(true);
  }, [currentBoard]);

  const handleConfirmRename = useCallback(() => {
    const trimmed = renameName.trim();
    if (!trimmed || !currentBoard) {
      setRenameOpen(false);
      return;
    }
    if (trimmed !== currentBoard.name) {
      onRenameBoard(currentBoard.id, trimmed);
    }
    setRenameOpen(false);
  }, [renameName, currentBoard, onRenameBoard]);

  const handleCreate = () => {
    if (!newName.trim()) return;
    onCreateBoard({ name: newName.trim(), template });
    setDialogOpen(false);
    setNewName("");
    setTemplate("status");
  };

  const handleDelete = () => {
    if (deleteConfirm) {
      onDeleteBoard(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  return (
    <div className={classes.root}>
      {/* Board name / selector */}
      <div className={classes.boardHeader}>
        <BoardIcon className={classes.boardIcon} />

        {boards.length > 1 ? (
          // Multiple boards → dropdown
          <FormControl variant="outlined" size="small" className={classes.select}>
            <InputLabel>Painel</InputLabel>
            <Select
              value={currentBoardId || ""}
              onChange={(e) => onSelectBoard(e.target.value)}
              label="Painel"
            >
              {boards.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                  {b.isShared ? " (compartilhado)" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : currentBoard ? (
          // Single board → display name, double-click to rename
          <Tooltip title="Duplo clique para renomear" placement="bottom">
            <div
              className={classes.boardNameDisplay}
              onDoubleClick={handleOpenRename}
            >
              <span className={classes.boardNameText}>
                {currentBoard.name}
              </span>
            </div>
          </Tooltip>
        ) : null}
      </div>

      {/* Action buttons */}
      <div className={classes.actionButtons}>
        {currentBoard && (
          <Tooltip title="Renomear painel">
            <IconButton size="small" onClick={handleOpenRename}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Criar novo painel">
          <IconButton size="small" onClick={() => setDialogOpen(true)}>
            <AddIcon />
          </IconButton>
        </Tooltip>

        {currentBoardId && boards.length > 1 && (
          <Tooltip title="Excluir painel">
            <IconButton
              size="small"
              onClick={() => setDeleteConfirm(currentBoardId)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {/* ── Rename Dialog ── */}
      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Renomear Painel</DialogTitle>
        <DialogContent>
          <TextField
            label="Nome do painel"
            variant="outlined"
            size="small"
            fullWidth
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleConfirmRename();
              }
            }}
            placeholder="Ex: Vendas, Suporte, Financeiro..."
            style={{ marginTop: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleConfirmRename}
            color="primary"
            variant="contained"
            disabled={!renameName.trim()}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Criar Novo Painel</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            label="Nome do painel"
            variant="outlined"
            size="small"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            placeholder="Ex: Vendas, Suporte, Financeiro..."
          />

          <Typography variant="subtitle2" color="textSecondary">
            Template
          </Typography>

          {TEMPLATES.map((t) => (
            <div
              key={t.id}
              className={`${classes.templateOption} ${
                template === t.id ? classes.templateSelected : ""
              }`}
              onClick={() => setTemplate(t.id)}
            >
              <Typography className={classes.templateTitle}>
                {t.title}
              </Typography>
              <Typography className={classes.templateDesc}>
                {t.desc}
              </Typography>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCreate}
            color="primary"
            variant="contained"
            disabled={!newName.trim()}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
      >
        <DialogTitle>Excluir Painel</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir este painel? Todas as colunas e
            configurações serão perdidas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BoardSelector;
